import * as React from 'react'
import { GripVertical, X, Plus, Pencil, MoreHorizontal } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { Button } from '@/components/ui/button'
import { SafeHtml } from '@/components/shared/safe-html'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CurrencyInput } from '@/components/ui/currency-input'
import { centsToDollars } from '@/lib/currency-utils'
import {
  QUOTE_ITEM_NO_TAX_VALUE,
  QUOTE_ITEM_PRICE_INPUT_CLASS,
  QuoteItemInlineSelect,
  QuoteItemDragHandle,
  QuoteItemList,
  QuoteItemListContainer,
  QuoteItemQuantityStepper,
  QuoteItemTwoLineRow,
  buildQuoteItemTaxOptions,
  buildQuoteItemUnitOptions,
  formatQuoteItemTaxSubLine,
  formatQuoteItemUnitLabel,
  getQuoteItemQuantityIncrement,
} from './quote-item-table'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { Item, TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { ItemKindEnum, ItemUnitEnum } from '@/gen/cortado/v1/settings_pb'
import { cn } from '@/lib/utils'
import { dollarsToCents, formatCurrency, formatDollars, formatTaxRateFraction } from '@/lib/currency-utils'
import { getDefaultAddonQuantity } from '@/lib/addon-defaults'
import { calculateLineTotalCents, resolveLineItemTaxFields } from '@/lib/line-item-calculations'
import { formatEnumName } from '@/lib/enum-utils'
import type { QuoteEventFormValue } from './quote-event-mapping'

import { QuoteAddonOfferEditSheet } from './quote-addon-offer-edit-sheet'

export interface QuoteAddonOfferFormValue {
  id?: string // Optional - only exists for saved quote revision add-on offers
  sourceItemId?: string // Optional - references items.id (catalog item this offer was seeded from)
  tempId?: string // Temporary ID for in-flight offers (used for drag-and-drop and updates)
  name: string
  description: string
  unitPriceCents: number
  unit: ItemUnitEnum
  quantity: number
  sortOrder: number
  isTaxable: boolean
  salesTaxRateId?: string
  salesTaxRateName?: string
  salesTaxRate?: number
}

// Helper function to get a unique ID for an add-on offer.
// Prefers the DB id (saved revisions), then tempId (in-flight rows).
export function getAddonOfferId(offer: QuoteAddonOfferFormValue): string {
  return offer.id || offer.tempId || `temp-${offer.sortOrder}`
}

/** A new add-on offer seeded from a catalog item, priced and sized as the catalog says. */
export function buildCatalogAddonOffer(
  item: Item,
  sortOrder: number,
  quoteEvents: QuoteEventFormValue[],
): QuoteAddonOfferFormValue {
  const unit = item.unit || ItemUnitEnum.ITEM_UNIT_PER_EVENT
  return {
    tempId: crypto.randomUUID(),
    sourceItemId: item.id,
    name: item.title,
    description: item.description || '',
    unitPriceCents: dollarsToCents(item.price),
    unit,
    quantity: getDefaultAddonQuantity(unit, quoteEvents),
    sortOrder,
    isTaxable: true,
  }
}

/**
 * While "Apply Sales Tax to All Items" is engaged, stamp its tax rate onto a
 * freshly created offer so every add path stays consistent with existing offers.
 * Add-on tax is calculated server-side (no salesTaxCents field here), so only
 * the rate fields are stamped.
 */
export function applyAutoAddonTax(
  offer: QuoteAddonOfferFormValue,
  autoApplyTaxRateId: string | undefined,
  taxRates: TaxRate[],
): QuoteAddonOfferFormValue {
  const autoTaxRate = autoApplyTaxRateId ? taxRates.find((rate) => rate.id === autoApplyTaxRateId) : undefined
  if (!autoTaxRate) return offer

  return {
    ...offer,
    isTaxable: true,
    salesTaxRateId: autoTaxRate.id,
    salesTaxRateName: autoTaxRate.name,
    salesTaxRate: autoTaxRate.percentage / 100,
  }
}

interface QuoteAddonOffersTableProps {
  offers: QuoteAddonOfferFormValue[]
  availableItems: Item[]
  availableTaxRates: TaxRate[]
  quoteEvents: QuoteEventFormValue[]
  onOffersChange: (offers: QuoteAddonOfferFormValue[]) => void
  disabled?: boolean
  // When set, "Apply Sales Tax to All Items" is engaged for this tax rate id -
  // every newly added offer (catalog) is created taxable with it.
  autoApplyTaxRateId?: string
  // Fired when the user manually sets an existing offer's tax to something other
  // than autoApplyTaxRateId, signalling the "apply to all" state no longer holds.
  onAutoApplyOverride?: () => void
  /** Renders without its own heading/empty state when hosted inside a labelled sub-section. */
  embedded?: boolean
  /**
   * `Item.id`s already present as a line item's `sourceItemId` on the quote.
   * sourceItemId is NOT unique (multiple line items can share one), so this
   * must stay Set-membership, never `.find`.
   */
  existingLineItemSourceIds?: Set<string>
}

function SortableOfferCard({
  offer,
  onEdit,
  onRemove,
  disabled,
}: {
  offer: QuoteAddonOfferFormValue
  onEdit: () => void
  onRemove: () => void
  disabled?: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: getAddonOfferId(offer) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  const offerName = offer.name || 'Unnamed Add-on'

  return (
    <li ref={setNodeRef} style={style}>
      <article className={cn('rounded-lg border bg-card p-3', isDragging && 'bg-muted')}>
        <div className="flex flex-wrap items-start gap-2">
          <button
            type="button"
            {...attributes}
            {...listeners}
            aria-label={`Reorder ${offerName}`}
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-grab hover:bg-muted hover:text-foreground active:cursor-grabbing',
            )}
            disabled={disabled}
          >
            <GripVertical className="h-4 w-4" />
          </button>

          <div className="min-w-40 flex-1 py-1.5">
            <p className="font-medium leading-5">{offerName}</p>
            {offer.description && (
              <SafeHtml
                html={offer.description}
                className="mt-1 text-sm text-muted-foreground line-clamp-2 whitespace-normal"
              />
            )}
          </div>

          <div className="ml-auto flex flex-wrap gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-11"
              onClick={onEdit}
              disabled={disabled}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11 text-destructive hover:text-destructive"
              onClick={onRemove}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
              Remove
            </Button>
          </div>
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-t pt-3 @[44rem]/quote-addons:grid-cols-5">
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Unit price</dt>
            <dd className="mt-1 font-financial text-sm tabular-nums">${formatCurrency(offer.unitPriceCents)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Quantity</dt>
            <dd className="mt-1 text-sm tabular-nums">{offer.quantity}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Unit</dt>
            <dd className="mt-1 text-sm">{formatEnumName(offer.unit, ItemUnitEnum)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Tax rate</dt>
            <dd className="mt-1 text-sm">
              {offer.isTaxable && offer.salesTaxRateName
                ? `${offer.salesTaxRateName} (${formatTaxRateFraction(offer.salesTaxRate || 0)}%)`
                : 'No tax'}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs font-medium text-muted-foreground">Line total</dt>
            <dd className="mt-1 font-financial text-sm font-semibold tabular-nums">
              ${formatCurrency(calculateLineTotalCents(offer.quantity, offer.unitPriceCents))}
            </dd>
          </div>
        </dl>
      </article>
    </li>
  )
}

function SortableOfferRow({
  offer,
  onEdit,
  onRemove,
  onQuantityChange,
  onUnitPriceChange,
  onUnitChange,
  onTaxRateChange,
  availableTaxRates,
  disabled,
}: {
  offer: QuoteAddonOfferFormValue
  onEdit: () => void
  onRemove: () => void
  onQuantityChange: (quantity: number) => void
  onUnitPriceChange: (unitPriceCents: number) => void
  onUnitChange: (unit: ItemUnitEnum) => void
  onTaxRateChange: (taxRateId: string | undefined) => void
  availableTaxRates: TaxRate[]
  disabled?: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: getAddonOfferId(offer) })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  const offerName = offer.name || 'Unnamed Add-on'
  // Shown only when it differs from the unit-price field on the row's second line.
  const offerTotalCents = calculateLineTotalCents(offer.quantity, offer.unitPriceCents)

  return (
    <li ref={setNodeRef} style={style} className={cn(isDragging && 'bg-muted')}>
      <QuoteItemTwoLineRow
        dragHandle={
          <QuoteItemDragHandle
            itemName={offerName}
            attributes={attributes}
            listeners={listeners}
            disabled={disabled}
          />
        }
        name={offerName}
        badge={
          <span className="mt-1 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary">
            Optional add-on
          </span>
        }
        totalLabel={offerTotalCents === offer.unitPriceCents
          ? undefined
          : `$${formatCurrency(offerTotalCents)}`}
        quantityControl={
          <QuoteItemQuantityStepper
            itemName={offerName}
            quantity={offer.quantity}
            increment={getQuoteItemQuantityIncrement(offer.unit)}
            disabled={disabled}
            onQuantityChange={onQuantityChange}
          />
        }
        metaItems={[
          <QuoteItemInlineSelect
            label={`Change unit for ${offerName}`}
            triggerLabel={formatQuoteItemUnitLabel(offer.unit)}
            value={String(offer.unit)}
            options={buildQuoteItemUnitOptions()}
            disabled={disabled}
            onSelect={(value) => onUnitChange(Number(value) as ItemUnitEnum)}
          />,
          <QuoteItemInlineSelect
            label={`Change tax rate for ${offerName}`}
            triggerLabel={formatQuoteItemTaxSubLine(offer)}
            value={offer.isTaxable && offer.salesTaxRateId ? offer.salesTaxRateId : QUOTE_ITEM_NO_TAX_VALUE}
            options={buildQuoteItemTaxOptions(availableTaxRates)}
            disabled={disabled}
            onSelect={(value) => onTaxRateChange(value === QUOTE_ITEM_NO_TAX_VALUE ? undefined : value)}
          />,
        ]}
        priceControl={
          <CurrencyInput
            value={centsToDollars(offer.unitPriceCents)}
            onChange={(dollars) => onUnitPriceChange(dollarsToCents(dollars))}
            disabled={disabled}
            aria-label={`Unit price for ${offerName}`}
            className={QUOTE_ITEM_PRICE_INPUT_CLASS}
          />
        }
        menu={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-muted-foreground"
                aria-label={`Actions for ${offerName}`}
                disabled={disabled}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
              <DropdownMenuItem onSelect={onRemove} variant="destructive">Remove</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
    </li>
  )
}

export function QuoteAddonOffersTable({
  offers,
  availableItems,
  availableTaxRates,
  quoteEvents,
  onOffersChange,
  disabled = false,
  autoApplyTaxRateId,
  onAutoApplyOverride,
  embedded = false,
  existingLineItemSourceIds,
}: QuoteAddonOffersTableProps) {
  const [open, setOpen] = React.useState(false)
  const [editingOffer, setEditingOffer] = React.useState<QuoteAddonOfferFormValue | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: disabled ? 999999 : 0, // Prevent drag when disabled
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    // Don't allow reordering if disabled
    if (disabled) return

    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = offers.findIndex((offer) => getAddonOfferId(offer) === active.id)
      const newIndex = offers.findIndex((offer) => getAddonOfferId(offer) === over.id)

      const newOffers = arrayMove(offers, oldIndex, newIndex)
      // Update sortOrder numbers
      const reorderedOffers = newOffers.map((offer, index) => ({
        ...offer,
        sortOrder: index,
      }))
      onOffersChange(reorderedOffers)
    }
  }

  const applyAutoTax = (newOffer: QuoteAddonOfferFormValue): QuoteAddonOfferFormValue =>
    applyAutoAddonTax(newOffer, autoApplyTaxRateId, availableTaxRates)

  const handleAddOffer = (selectedItem: Item) => {
    onOffersChange([...offers, applyAutoTax(buildCatalogAddonOffer(selectedItem, offers.length, quoteEvents))])
    setOpen(false)
  }

  const handleAddCustomOffer = () => {
    const newOffer: QuoteAddonOfferFormValue = {
      name: '',
      description: '',
      unitPriceCents: 0,
      unit: ItemUnitEnum.ITEM_UNIT_PER_EVENT,
      quantity: 1,
      sortOrder: offers.length,
      isTaxable: true,
    }
    setEditingOffer(applyAutoTax(newOffer))
  }

  const handleSaveOffer = (savedOffer: QuoteAddonOfferFormValue) => {
    // Manually diverging one offer from the auto-applied rate means "all offers"
    // no longer holds - let the caller disengage it rather than keep taxing
    // future new offers under a stale assumption.
    if (autoApplyTaxRateId && savedOffer.salesTaxRateId !== autoApplyTaxRateId) {
      onAutoApplyOverride?.()
    }
    const offerId = getAddonOfferId(savedOffer)
    const existingIndex = offers.findIndex((offer) => getAddonOfferId(offer) === offerId)

    if (existingIndex >= 0) {
      onOffersChange(offers.map((offer, index) =>
        index === existingIndex ? savedOffer : offer
      ))
      return
    }

    onOffersChange([
      ...offers,
      {
        ...savedOffer,
        tempId: savedOffer.tempId || crypto.randomUUID(),
        sortOrder: offers.length,
      },
    ])
  }

  const handleOfferQuantityChange = (offerId: string, quantity: number) => {
    onOffersChange(offers.map((offer) =>
      getAddonOfferId(offer) === offerId ? { ...offer, quantity } : offer
    ))
  }

  const handleOfferUnitPriceChange = (offerId: string, unitPriceCents: number) => {
    onOffersChange(offers.map((offer) =>
      getAddonOfferId(offer) === offerId ? { ...offer, unitPriceCents } : offer
    ))
  }

  const handleOfferUnitChange = (offerId: string, unit: ItemUnitEnum) => {
    onOffersChange(offers.map((offer) =>
      getAddonOfferId(offer) === offerId ? { ...offer, unit } : offer
    ))
  }

  const handleOfferTaxRateChange = (offerId: string, taxRateId: string | undefined) => {
    // Manually diverging one offer from the auto-applied rate means "all
    // offers" no longer holds, exactly as the edit sheet's save path signals.
    if (autoApplyTaxRateId && taxRateId !== autoApplyTaxRateId) {
      onAutoApplyOverride?.()
    }
    const selectedRate = taxRateId ? availableTaxRates.find((rate) => rate.id === taxRateId) : undefined
    onOffersChange(offers.map((offer) =>
      getAddonOfferId(offer) === offerId
        ? { ...offer, ...resolveLineItemTaxFields(selectedRate) }
        : offer
    ))
  }

  const handleRemoveOffer = (offerId: string) => {
    const newOffers = offers
      .filter((offer) => getAddonOfferId(offer) !== offerId)
      .map((offer, index) => ({ ...offer, sortOrder: index }))
    onOffersChange(newOffers)
  }

  // Any standard catalog item can be manually offered as an add-on.
  // Transportation items are excluded - the backend rejects them as add-on
  // offers (validateStandardCatalogItems). Also exclude items already offered
  // or already present as a quote line item.
  const availableToAdd = availableItems.filter(
    (item) =>
      item.itemKind !== ItemKindEnum.TRANSPORTATION
      && !offers.some((offer) => offer.sourceItemId === item.id)
      && !existingLineItemSourceIds?.has(item.id)
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {embedded ? <span /> : (
          <div>
            <h3 className="text-sm font-medium">Optional Add-ons</h3>
            <p className="text-xs text-muted-foreground">Curate the add-ons customers can select in the portal</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleAddCustomOffer}
            disabled={disabled}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Custom Add-on
          </Button>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button type="button" size="sm" disabled={disabled || availableToAdd.length === 0}>
                <Plus className="h-4 w-4 mr-1" />
                Add Add-on
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="end">
              <Command>
                <CommandInput placeholder="Search add-ons..." />
                <CommandList>
                  <CommandEmpty>No add-ons found.</CommandEmpty>
                  <CommandGroup>
                    {availableToAdd.map((item) => (
                      <CommandItem
                        key={item.id}
                        onSelect={() => handleAddOffer(item)}
                        className="cursor-pointer"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{item.title}</span>
                          {item.description && (
                            <SafeHtml
                              html={item.description}
                              className="text-xs text-muted-foreground line-clamp-1"
                            />
                          )}
                          <span className="text-xs text-muted-foreground">${formatDollars(item.price)}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {offers.length === 0 ? (
        embedded ? null : (
          <div className="border border-dashed rounded-lg p-12 text-center">
            <p className="text-sm text-muted-foreground">No add-ons offered yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click &quot;Add Add-on&quot; to get started
            </p>
          </div>
        )
      ) : embedded ? (
        <QuoteItemListContainer>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={offers.map((offer) => getAddonOfferId(offer))}
              strategy={verticalListSortingStrategy}
            >
              <QuoteItemList>
                {offers.map((offer) => (
                  <SortableOfferRow
                    key={getAddonOfferId(offer)}
                    offer={offer}
                    onEdit={() => setEditingOffer(offer)}
                    onRemove={() => handleRemoveOffer(getAddonOfferId(offer))}
                    onQuantityChange={(quantity) => handleOfferQuantityChange(getAddonOfferId(offer), quantity)}
                    onUnitPriceChange={(unitPriceCents) => handleOfferUnitPriceChange(getAddonOfferId(offer), unitPriceCents)}
                    onUnitChange={(unit) => handleOfferUnitChange(getAddonOfferId(offer), unit)}
                    onTaxRateChange={(taxRateId) => handleOfferTaxRateChange(getAddonOfferId(offer), taxRateId)}
                    availableTaxRates={availableTaxRates}
                    disabled={disabled}
                  />
                ))}
              </QuoteItemList>
            </SortableContext>
          </DndContext>
        </QuoteItemListContainer>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <ul className="@container/quote-addons space-y-3">
            <SortableContext items={offers.map((offer) => getAddonOfferId(offer))} strategy={verticalListSortingStrategy}>
              {offers.map((offer) => (
                <SortableOfferCard
                  key={getAddonOfferId(offer)}
                  offer={offer}
                  onEdit={() => setEditingOffer(offer)}
                  onRemove={() => handleRemoveOffer(getAddonOfferId(offer))}
                  disabled={disabled}
                />
              ))}
            </SortableContext>
          </ul>
        </DndContext>
      )}

      {/* Edit Sheet */}
      <QuoteAddonOfferEditSheet
        offer={editingOffer}
        availableTaxRates={availableTaxRates}
        open={!!editingOffer}
        onOpenChange={(open) => {
          if (!open) setEditingOffer(null)
        }}
        onSave={handleSaveOffer}
        disabled={disabled}
      />
    </div>
  )
}
